import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { useFeatureToggle } from '@flopflip/react-redux';
import { PipelinesPaths } from 'app/configs/routesConfig/pipelines';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import withPagePermissions from 'hocs/withPagePermissions';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { FeatureFlag } from 'constants/featureFlags';
import { LoanApplicationTabRouteParam } from 'constants/loanApplication';
import { PAGE_CODES } from 'constants/pageCodes';
import useCompanyId from 'hooks/useCompanyId';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import usePendingChangesDependentFetch from 'hooks/usePendingChangesDependentFetch';
import useReleaseFieldLocksOnTabChange from 'hooks/useReleaseFieldLocksOnTabChange';
import { buildDocumentLoanParams } from 'utils/documentPanes';

import { selectIsSubCompanyUser } from 'store/auth/selectors';
import { getPropertiesAction } from 'store/companies/actions';
import { loadCustomFieldsAction } from 'store/customFieldDefinitions/actions';
import {
  clearEditStateAction,
  getApplicationChangesHistoryAction,
  getApplicationLockedFields,
  getAusSubmitOperationsAction,
  getAusSummaryAction,
  getLoanApplicationChannelInfoByApplicationIdAction,
  getLoanApplicationCommonDataByApplicationIdAction,
  getLoanApplicationHeaderViewAction,
} from 'store/loanApplications/actions';
import { loadCustomFieldValuesAction } from 'store/loanApplications/actions/customFieldValues.actions';
import { setLoanSavingRelatedDataAction } from 'store/loanApplications/actions/pendingChanges.actions';
import {
  selectActiveTabReFetchNeededData,
  selectCommonInfoLoanApplication,
  selectDealFromApplication,
  selectHasPendingCustomFieldChangesByApplicationId,
  selectIsCommonInfoLoanApplicationFullyLoaded,
  selectLoanPendingChanges,
} from 'store/loanApplications/selectors';
import { getMilestonePresentationSettingsAction } from 'store/milestoneDefinitions/actions';
import { getChannelsAction } from 'store/programs/actions';

import LoanApplicationSignalRContainer from './LoanApplicationSignalRContainer';
import LoanEditorSplitLayout from './LoanEditorSplitLayout';
import LoanDocumentsPaneContent from './LoanEditorSplitLayout/LoanDocumentsPaneContent';
import {
  Activities,
  AiActivity,
  Borrowers,
  Checklists,
  Construction,
  Exceptions,
  FinancialInfo,
  LoanClosingCDS,
  LoanDocumentsTab,
  LoanFees,
  LoanSummary,
  Parties,
  PathToSubmission,
  PostClosing,
  PropertyAndLoan,
  Servicing,
  Tasks,
  Underwriting,
  UnderwritingCDS,
  VendorRequests,
} from './tabs';
import Alerts from './tabs/alerts';

// TODO: make all restyled tabs without remount just for refetching as it's bad practices (renderLoanApplicationTab function)
const tabsWithoutRemountOnRefetch = [
  LoanApplicationTabRouteParam.PROPERTY_AND_LOAN,
  LoanApplicationTabRouteParam.CONTACTS,
  LoanApplicationTabRouteParam.LOAN_SUMMARY,
  LoanApplicationTabRouteParam.FINANCIAL_INFO,
  LoanApplicationTabRouteParam.BORROWERS,
  LoanApplicationTabRouteParam.UNDERWRITING_NEW,
  LoanApplicationTabRouteParam.CLOSING_NEW,
  LoanApplicationTabRouteParam.POST_CLOSING,
];

const loanApplicationSideDataAction =
  ({ applicationId, companyId, includeLoanChangesHistory = true }) =>
  dispatch =>
    Promise.all([
      includeLoanChangesHistory && dispatch(getApplicationChangesHistoryAction({ applicationId })),
      dispatch(getLoanApplicationHeaderViewAction({ applicationId })),
      dispatch(getApplicationLockedFields({ applicationId })),
      dispatch(getPropertiesAction({ companyId })),
      dispatch(getChannelsAction()),
      dispatch(getLoanApplicationChannelInfoByApplicationIdAction({ applicationId, companyId })),
      dispatch(getMilestonePresentationSettingsAction()),
      dispatch(loadCustomFieldValuesAction({ applicationId })),
      dispatch(
        loadCustomFieldsAction({
          companyId,
          enabledOnly: true,
        }),
      ),
    ]);

const LoanApplicationContent = ({ activeTab, setIsLoading }) => {
  const dispatch = useDispatch();

  useReleaseFieldLocksOnTabChange(activeTab);

  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const history = useHistory();
  const { applicationId: applicationIdFromParams } = useParams();

  const deal = useSelector(selectDealFromApplication);
  const application = useSelector(selectCommonInfoLoanApplication);
  const isCommonDataFullyLoaded = useSelector(selectIsCommonInfoLoanApplicationFullyLoaded);
  const isSubCompanyUser = useSelector(selectIsSubCompanyUser);
  const { isActiveTabReFetchNeeded, triggeredByPendingChanges } = useSelector(
    selectActiveTabReFetchNeededData,
  );

  const isNewAlertsPage = useFeatureToggle(FeatureFlag.ShowNewAlertsPage);
  const isRestyledUnderwritingTabEnabled = useFeatureToggle(
    FeatureFlag.EnableRestyledUnderwritingTab,
  );

  const loanId = deal?.loanId;
  const companyId = useCompanyId();

  const applicationId = applicationIdFromParams || deal?.applicationId;

  const documentPaneLoanParams = useMemo(
    () => buildDocumentLoanParams({ loanId, applicationId }),
    [loanId, applicationId],
  );

  const renderDocumentsPane = useCallback(
    () => <LoanDocumentsPaneContent loanParams={documentPaneLoanParams} />,
    [documentPaneLoanParams],
  );

  // A subtree-scoped TPO broker gets a READ-ONLY editor off the reads it is authorized for
  // (getLoanApplication + common-info + milestones). Its secondary reads — lock-info in
  // particular — legitimately 403, so `getLoanApplicationCommonData`'s Promise.all rejects on the
  // lock-info 403 and flips isDataLoaded before common-info's 200 populates the slice; and
  // `isCommonDataFullyLoaded` (which needs isLockInfoLoaded) never becomes true for a broker.
  // Either would bounce every broker straight to the pipeline. A broker therefore never auto-
  // redirects out of the editor — it renders off whatever authorized reads resolve (DEV-4226).
  const isInvalidApplication =
    isDataLoaded && !isSubCompanyUser && (_.isEmpty(application) || !isCommonDataFullyLoaded);

  const selectHasPendingCustomFieldChanges = useMemo(
    () => selectHasPendingCustomFieldChangesByApplicationId(applicationId),
    [applicationId],
  );
  const hasPendingCustomFieldChanges = useSelector(selectHasPendingCustomFieldChanges);
  const loanPendingChanges = useSelector(selectLoanPendingChanges);

  const refetchCustomFieldValues = useCallback(() => {
    if (!applicationId) {
      return;
    }

    dispatch(loadCustomFieldValuesAction({ applicationId }));
  }, [applicationId, dispatch]);

  usePendingChangesDependentFetch(refetchCustomFieldValues);

  useEffect(() => {
    if (!isInvalidApplication) {
      return;
    }

    history.push(PipelinesPaths.loanPipelines.path);
  }, [isInvalidApplication, history]);

  useEffect(() => {
    if (!hasPendingCustomFieldChanges || loanPendingChanges?.isChangesNotSaved) {
      return;
    }

    dispatch(
      setLoanSavingRelatedDataAction({
        metadata: {
          hasPendingChanges: true,
          hasPendingChangesByCurrentUser:
            loanPendingChanges?.hasPendingChangesByCurrentUser ?? true,
          isProgramChangeRequired: loanPendingChanges?.isProgramChangeRequired ?? false,
          pendingChangesMadeByUsers: loanPendingChanges?.pendingChangesAuthors ?? null,
        },
      }),
    );
  }, [hasPendingCustomFieldChanges, loanPendingChanges, dispatch]);

  useEffect(() => {
    dispatch(clearEditStateAction());
  }, [activeTab, dispatch]);

  useEffectSafeStrictMode(() => {
    setIsLoading(true);

    Promise.all([
      dispatch(getLoanApplicationCommonDataByApplicationIdAction({ applicationId })).finally(() =>
        setIsDataLoaded(true),
      ),
      dispatch(loanApplicationSideDataAction({ applicationId, companyId })),
    ])
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, setIsLoading, applicationId, companyId]);

  useEffectSafeStrictMode(() => {
    if (!isActiveTabReFetchNeeded) {
      return;
    }

    setIsLoading(true);

    Promise.all([
      !triggeredByPendingChanges &&
        dispatch(getLoanApplicationCommonDataByApplicationIdAction({ applicationId })),
      dispatch(
        loanApplicationSideDataAction({
          applicationId,
          companyId,
          includeLoanChangesHistory: !triggeredByPendingChanges,
        }),
      ),
    ])
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [
    dispatch,
    setIsLoading,
    applicationId,
    isActiveTabReFetchNeeded,
    companyId,
    triggeredByPendingChanges,
  ]);

  useEffectSafeStrictMode(() => {
    if (!loanId) {
      return;
    }

    dispatch(getAusSummaryAction({ loanId })).catch(Error);
  }, [dispatch, loanId]);

  useEffectSafeStrictMode(() => {
    if (!loanId) {
      return;
    }

    dispatch(getAusSubmitOperationsAction({ loanId, companyId }));
  }, [dispatch, companyId, loanId]);

  if (isInvalidApplication) {
    return null;
  }

  const renderLoanApplicationTab = () => {
    const isTabExemptFromRemount =
      tabsWithoutRemountOnRefetch.includes(activeTab) && !triggeredByPendingChanges;

    if (isActiveTabReFetchNeeded && !isTabExemptFromRemount) {
      // Re-mount tab and run effects for request data.
      // Restyled tabs normally skip remount, but pending-changes saves
      // require a full remount so the tab re-fetches its own data.
      return null;
    }

    switch (activeTab) {
      case LoanApplicationTabRouteParam.LOAN_SUMMARY:
        return <LoanSummary />;

      case LoanApplicationTabRouteParam.TASKS:
        return <Tasks />;

      case LoanApplicationTabRouteParam.ACTIVITIES:
        return <Activities />;

      case LoanApplicationTabRouteParam.AI_ACTIVITY:
        return <AiActivity />;

      case LoanApplicationTabRouteParam.UNDERWRITING:
        // DEV-5501: the legacy underwriting surface carries the DEV-5204-class broker-path bug.
        // Its nav tab is hidden once the restyled tab is on, but a bookmark/deep link to this
        // route would still reach it — send migrated tenants to the restyled surface instead.
        return isRestyledUnderwritingTabEnabled ? <UnderwritingCDS /> : <Underwriting />;

      case LoanApplicationTabRouteParam.UNDERWRITING_NEW:
        return <UnderwritingCDS />;

      case LoanApplicationTabRouteParam.DOCUMENTS:
        return <LoanDocumentsTab />;

      case LoanApplicationTabRouteParam.CLOSING_NEW:
        return <LoanClosingCDS />;

      case LoanApplicationTabRouteParam.POST_CLOSING:
        return <PostClosing />;

      case LoanApplicationTabRouteParam.CONSTRUCTION:
        return <Construction />;

      case LoanApplicationTabRouteParam.BORROWERS:
        return <Borrowers />;

      case LoanApplicationTabRouteParam.FINANCIAL_INFO:
        return <FinancialInfo />;

      case LoanApplicationTabRouteParam.LOAN_FEES:
        return <LoanFees />;

      case LoanApplicationTabRouteParam.ALERTS:
        return isNewAlertsPage ? <Alerts /> : <Checklists />;

      case LoanApplicationTabRouteParam.PROPERTY_AND_LOAN:
        return <PropertyAndLoan />;

      case LoanApplicationTabRouteParam.CONTACTS:
        return <Parties />;

      case LoanApplicationTabRouteParam.SERVICING:
        return <Servicing />;

      case LoanApplicationTabRouteParam.LOAN_EXCEPTIONS:
        return <Exceptions />;

      case LoanApplicationTabRouteParam.VENDOR_REQUESTS:
        return <VendorRequests />;

      default:
        return null;
    }
  };

  // The SignalR container owns a module-singleton hub connection it starts/stops on mount/unmount,
  // so it must stay mounted across document-pane open/close. Keep it OUTSIDE the split layout (the
  // stable ancestor) — wrapping it inside would remount it whenever panes wrap/unwrap the editor,
  // double-starting the live hub ("Cannot start a HubConnection that is not in the 'Disconnected'
  // state").
  return (
    <LoanApplicationSignalRContainer>
      <LoanEditorSplitLayout renderDocuments={renderDocumentsPane}>
        <PathToSubmission />
        {renderLoanApplicationTab()}
      </LoanEditorSplitLayout>
    </LoanApplicationSignalRContainer>
  );
};

LoanApplicationContent.propTypes = {
  ...WithLoadingPropTypes,
  activeTab: PropTypes.string.isRequired,
};

export default compose(
  withLoading,
  withPagePermissions(PAGE_CODES.LOAN_APPLICATION),
  memo,
)(LoanApplicationContent);
